import styles from './FooterSecondaryNavDesktop.scss';

import React, { FC, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { ExternalLink, InternalLink, LinkSet } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Grid } from 'src/components/Grid/Grid';
import { ResizeObserverTargetSize, initialResizeObserverTargetSize, useResizeObserver } from 'src/hooks/UseResizeObserver';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { componentKeyV2 } from 'src/utils/ComponentKeyV2';

import { TermsAndConditions } from '../../TermsAndConditions/TermsAndConditions';
import { FooterLegalLinks } from '../FooterLegalLinks/FooterLegalLinks';
import { FooterLinkSet } from '../FooterLinkSet/FooterLinkSet';
import { FooterLocalePicker } from '../FooterLocalePicker/FooterLocalePicker';

export interface FooterSecondaryNavDesktopProps {
  items?: Array<FlatEntry<LinkSet>>;
  legalLinks?: Array<FlatEntry<ExternalLink | InternalLink>>;
  isCompact?: boolean;
  onItemsResizeUpdate?: (size: ResizeObserverTargetSize) => void;
}

export const FooterSecondaryNavDesktop: FC<FooterSecondaryNavDesktopProps> = ({
  items = [],
  legalLinks = [],
  isCompact,
  onItemsResizeUpdate,
}) => {
  const [size, setSize] = useState<ResizeObserverTargetSize>(initialResizeObserverTargetSize);
  const { ref: rootRef, inView } = useInView();
  const itemsRef = useRef<HTMLDivElement>(null);
  useResizeObserver({ targetRef: itemsRef, callback: s => setSize(s) });

  useEffect(() => {
    if (inView && onItemsResizeUpdate) onItemsResizeUpdate(size);
  }, [size.height, onItemsResizeUpdate, inView]);

  return (
    <Grid item xs={isCompact ? 12 : 9} className={styles.footerSecondaryNavDesktop} ref={rootRef}>
      <Grid container justifyContent="center" className={styles.container}>
        <Grid item xs={11} container gutter ref={itemsRef}>
          {items.map((entry, index) => (
            <Grid key={componentKeyV2(entry, index)} item xs={3}>
              <FooterLinkSet entry={entry} titleClassName={styles.title} listItemClassName={styles.listItem} />
            </Grid>
          ))}
          <Grid item xs="auto">
            <TermsAndConditions className={styles.disclosureBlurbWrapper} />
          </Grid>
        </Grid>
        <Grid item xs={11} container gutter>
          <Grid item xs>
            <FooterLegalLinks items={legalLinks} className={styles.legalLinks} />
          </Grid>
          <Grid item xs="auto">
            <FooterLocalePicker className={styles.localePicker} />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

FooterSecondaryNavDesktop.displayName = 'FooterSecondaryNavDesktop';
